// StoreSwapAnimation.tsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import anime from 'animejs';

const translations = {
  en: {
    stages: ['Original', '', '', 'Wycon', 'Adidas', 'Marie Blachere', 'Aura', 'Fnac'],
  },
  fr: {
    stages: ['Original', '', '', 'Wycon', 'Adidas', 'Marie Blachere', 'Aura', 'Fnac'],
  },
};

type StoreSwapStage = {
  src: string;
  width?: number;
  label?: string;
};

const showcaseStages: StoreSwapStage[] = [
  { src: '/images/store-swap-before.webp', width: 1.0 },
  { src: '/images/store-swap-stage1.webp', width: 0.15 },
  { src: '/images/store-swap-stage2.webp', width: 0.15 },
  { src: '/images/store-swap-stage3.webp', width: 1.0 },
  { src: '/images/store-swap-adidas.webp', width: 0.35 },
  { src: '/images/store-swap-marieblachere.webp', width: 0.35 },
  { src: '/images/store-swap-aura.webp', width: 0.35 },
  { src: '/images/store-swap-fnac.webp', width: 0.35 },
  { src: '/images/store-swap-kfc.webp', width: 0.35 },
  { src: '/images/store-swap-berserk.webp', width: 0.35 },
  { src: '/images/store-swap-jimmy.webp', width: 0.35 },
];

interface StoreSwapAnimationProps {
  lang: 'en' | 'fr';
  className?: string;
  stages?: StoreSwapStage[];
  maxHeight?: string;
}

const StoreSwapAnimation: React.FC<StoreSwapAnimationProps> = ({ lang, className, stages, maxHeight }) => {
  const t = translations[lang];
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<anime.AnimeInstance | null>(null);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  const resolvedStages = useMemo(
    () =>
      (stages && stages.length > 0 ? stages : showcaseStages).map((stage) => ({
        ...stage,
        width: stage.width ?? 1,
      })),
    [stages],
  );
  const totalUniqueRelativeWidth = useMemo(
    () => resolvedStages.reduce((sum, stage) => sum + (stage.width ?? 1), 0),
    [resolvedStages],
  );
  const sliceCount = resolvedStages.length;
  const visibleSlices = useMemo(() => [...resolvedStages, ...resolvedStages], [resolvedStages]);

  useEffect(() => {
    if (!resolvedStages.length || !resolvedStages[0]) return;
    const img = new Image();
    img.onload = () => {
      setAspectRatio(img.naturalWidth / img.naturalHeight);
    };
    img.src = resolvedStages[0].src;
  }, [resolvedStages]);

  useEffect(() => {
    if (!aspectRatio || !containerRef.current) return;

    const container = containerRef.current;
    let isMounted = true;

    const observer = new ResizeObserver(entries => {
      if (!isMounted || !entries[0] || entries[0].contentRect.width === 0) return;

      if (animationRef.current) {
        animationRef.current.pause();
      }

      const wrapper = container.querySelector('.slices-wrapper') as HTMLElement;
      const images = Array.from(container.querySelectorAll('.compensating-image')) as HTMLElement[];
      if (!wrapper || images.length === 0) return;

      const containerWidth = container.getBoundingClientRect().width;
      const totalUniquePixelWidth = totalUniqueRelativeWidth * containerWidth;

      // Set widths in pixels to avoid browser rounding inconsistencies with percentages
      const slices = Array.from(wrapper.children) as HTMLElement[];
      wrapper.style.width = `${totalUniquePixelWidth * 2}px`;
      slices.forEach((slice, i) => {
        const stage = visibleSlices[i];
        if (stage) {
          slice.style.width = `${(stage.width ?? 1) * containerWidth}px`;
        }
      });

      let cumulativeOffset = 0;
      const sliceStartPositions = visibleSlices.map(stage => {
        const startPosition = cumulativeOffset;
        cumulativeOffset += (stage.width ?? 1) * containerWidth;
        return startPosition;
      });

      const setInitialPositions = () => {
        anime.set(wrapper, { translateX: 0 });
        anime.set(images, {
          translateX: (el: HTMLElement, i: number) => -sliceStartPositions[i]!,
        });
      };
      
      setInitialPositions();

      const animate = () => {
        animationRef.current = anime({
          targets: [wrapper, ...images],
          translateX: (element: HTMLElement, index: number) => {
            if (index === 0) return `-=${totalUniquePixelWidth}`;
            return `+=${totalUniquePixelWidth}`;
          },
          duration: 5000 * totalUniqueRelativeWidth,
          easing: 'linear',
          complete: () => {
            if (isMounted) {
              setInitialPositions();
              animate();
            }
          },
        });
      };

      animate();
      observer.disconnect();
    });

    observer.observe(container);

    return () => {
      isMounted = false;
      if (animationRef.current) {
        animationRef.current.pause();
      }
      observer.disconnect();
    };
  }, [aspectRatio, sliceCount, totalUniqueRelativeWidth, visibleSlices]);

  return (
    <div
      ref={containerRef}
      className={`w-full rounded-md overflow-hidden relative bg-black/30 transition-opacity duration-300 ${className}`}
      style={{
        aspectRatio: aspectRatio ? `${aspectRatio}` : '1.3333',
        opacity: aspectRatio ? 1 : 0,
        maxHeight: maxHeight,
        maxWidth: aspectRatio && maxHeight ? `calc(${maxHeight} * ${aspectRatio})` : undefined,
      }}
    >
      <div
        className="slices-wrapper absolute top-0 left-0 h-full flex"
        style={{ width: `${totalUniqueRelativeWidth * 2 * 100}%` }}
      >
        {visibleSlices.map((stage, i) => (
          <div
            key={i}
            className="h-full relative overflow-hidden"
            style={{
              flexShrink: 0,
              flexGrow: 0,
              flexBasis: `${(stage.width / (totalUniqueRelativeWidth * 2)) * 100}%`,
            }}
          >
            <img
              src={stage.src}
              alt={stage.label ?? t.stages[i % sliceCount] ?? ''}
              title={stage.label ?? t.stages[i % sliceCount] ?? ''}
              className="compensating-image absolute top-0 left-0 h-full"
              style={{
                width: `${(1 / (stage.width ?? 1)) * 100}%`,
                maxWidth: 'none',
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default React.memo(StoreSwapAnimation);
